import React, { useContext } from 'react';
import classnames from 'classnames';
import ReactMarkdown from 'react-markdown';
import flow from 'lodash/flow';

import { KIND, SIZE } from '@letsdothis/lds/lib/Button';
import { ButtonLink } from '@letsdothis/lds/lib/ButtonLink';

import getUrl from '@/routes/routes/helpers/getUrl';

import DiscoveryContextV2 from '../../../../contexts/discoveryContext/contextV2';

// Shared Components
import Faqs from '../../../../shared/Faqs';
import ReadMore from '../../../../components/ReadMore';
import Loading from '../../../../shared/Loading';
import Footer from '../../../../components/Footer';
import Container from '../../../../components/Container';

// Category Components
import EventList from '../../components/EventList';
import CategoryBreadcrumbs from '../../components/CategoryBreadcrumbs';
import TileNavigation from '../../components/TileNavigation';
import RelatedLinks from '../../components/RelatedLinks';

import Sort from '../../components/Sort';
import ParentMobile from '../../components/ParentMobile';
import TimingEventsSlice from '../../components/TimingEventsSlice';
import SportSlice from '../../components/SportSlice';
import NearbyEvents from '../../components/NearbyEvents';

import { isMobileOrTablet } from '../../../../utils/browserDetection';
import { getNavigationLinksAndLabels } from '../../utils/navigationLinksLabels';
import { getCategoryLabel, getParentLinks } from './helpers';

import { withLocationInfo, withSearchInfo } from '../../provider';
import eventResolver from '../../../../state/resolvers/event';

import ThinResult from '../../ThinResult';

import {
  answer,
  breadcrumbs,
  categoryDescription as categoryDescriptionStyle,
  eventsFound,
  eventsFoundTitle,
  headerSection,
  headerTextStyle,
  list,
  mobileOnly,
  question,
  shortDescription as shortDescriptionStyle,
  mobileHeaderContainer,
  outerContainer,
  outerContainerMapMode,
  mainContent,
  headerSpace,
  footer as footerStyles,
  socialLinkImage,
  socialLinkText,
  socialLinkWrapper,
} from './styles.module.css';

const BrowseLayout = ({
  query,
  dataLoading,
  categoryData,
  searchParams: { name: textSearchTerm } = {},

  // Properties that could be moved to context
  // some of these properties are also used in parent (Category Page).
  locationLinks,
  locationLinksWithNearMe,
  onDescriptionExpand, // tracking click
  trackingSource,
  scrollViewToTop,
  currentPage,
  resultsPerPage,

  // WithLocation HOC
  injected: { location: { isLocationSetByUser } = {} },

  // Hides Sport Slices, Groupings, Near Me;
  isGroupingsHidden = false,
  isHeaderHidden = false,
  onEventHoverOn,
  onEventHoverOff,
  mapMode = false,
}) => {
  const {
    content: {
      pageInfo: { description, shortDescription, title: headerText },
    },
    labels,
    parents,
    faqs,
    locationChildren,
  } = categoryData;

  const {
    filters: {
      distance: { selectedDistances },
      location: { name: selectedLocationName },
    },
  } = useContext(DiscoveryContextV2) ?? {};

  const isVirtual = categoryData?.query?.virtual;

  const rawEventsResult = categoryData.eventsV2 || categoryData.events || {};
  const totalResults = rawEventsResult?.total;
  const events =
    (rawEventsResult.events && rawEventsResult.events.map(eventResolver)) || [];

  const nearbyEvents =
    rawEventsResult?.nearbyEvents?.map?.(eventResolver) ?? [];

  const inThinResultsMode = !events.length || !!nearbyEvents.length;

  const spotlightEvents = categoryData.spotlightEvents?.events?.events?.map?.(
    eventResolver,
  );

  const categoryLabel = getCategoryLabel(labels);
  const parentLinks = getParentLinks(parents);

  /* Hides Nearby Events when user sets location */
  const NearByComponent = !isVirtual && !isLocationSetByUser && (
    <NearbyEvents searchParams={query} sport={headerText} />
  );

  // Show header when nearby events or spotlight events are present.
  const showHeaderText = !!NearByComponent || spotlightEvents?.length > 0;
  const textList = headerText?.split(' in ') ?? [];
  const formatedHeaderText =
    textList.length > 1
      ? `${textList[0].toLowerCase()} in ${textList[1]}`
      : headerText;

  const isMobile = isMobileOrTablet();
  const selectedLocationText = selectedLocationName
    ?.toLowerCase()
    ?.includes('custom')
    ? 'this area'
    : selectedLocationName;

  // Formatted Title
  const locationText = isLocationSetByUser && selectedLocationText;

  const titleWithLocation =
    locationText && !headerText.includes(locationText) // doesn't double add location (i.e. in-london url path)
      ? `${headerText} near ${locationText}`
      : headerText;

  const formattedTitleHeader =
    selectedDistances?.length === 1 &&
    !titleWithLocation.includes(selectedDistances[0]?.label)
      ? `${selectedDistances[0]?.label} ${titleWithLocation}`
      : titleWithLocation;

  const { distanceLinks } = getNavigationLinksAndLabels({ categoryData });

  const ThinResultProps = {
    query,
    mapMode,
    nearbyEvents,
    trackingSource,
    dataLoading,
    headerText,
    description,
    specificEvents: events,
  };

  if (inThinResultsMode) return <ThinResult {...ThinResultProps} />;

  return (
    <>
      <Container
        className={classnames(outerContainer, {
          [outerContainerMapMode]: mapMode,
        })}
      >
        <div className={mainContent}>
          {dataLoading ? (
            <Loading />
          ) : (
            <>
              {(!isGroupingsHidden || !isHeaderHidden) && (
                <>
                  {!isGroupingsHidden && (
                    <div
                      className={classnames(mobileHeaderContainer, mobileOnly)}
                    >
                      <ParentMobile parentLinks={parentLinks} />
                    </div>
                  )}

                  {!isMobile && (
                    <CategoryBreadcrumbs
                      className={breadcrumbs}
                      categoryData={categoryData}
                    />
                  )}

                  <div className={headerSection}>
                    {!isHeaderHidden && (
                      <>
                        <h1 className={headerTextStyle}>
                          {textSearchTerm
                            ? `Results for '${textSearchTerm}'`
                            : formattedTitleHeader}
                        </h1>
                        {shortDescription && currentPage <= 1 ? (
                          <ReadMore
                            className={shortDescriptionStyle}
                            content={shortDescription}
                            lineCount={3}
                            lineHeightValue={22}
                            lineHeightUnit="px"
                            onExpand={onDescriptionExpand}
                          />
                        ) : (
                          <div className={headerSpace} />
                        )}
                      </>
                    )}

                    {!isGroupingsHidden && (
                      <>
                        {!textSearchTerm && (
                          <>
                            {NearByComponent}
                            {!isVirtual && <TimingEventsSlice />}
                            <div className={socialLinkWrapper}>
                              <img
                                className={socialLinkImage}
                                src="https://res.cloudinary.com/letsdothiscom/image/upload/v1608137378/social/squad.png"
                                alt="friends"
                              />
                              <span className={socialLinkText}>
                                Finding an event for a group?
                              </span>
                              <ButtonLink
                                rel="nofollow"
                                href={getUrl.socialPageWithVariant('discover')}
                                kind={KIND.secondary}
                                size={SIZE.small}
                              >
                                Get started
                              </ButtonLink>
                            </div>
                            {!isVirtual && <SportSlice />}
                          </>
                        )}

                        {!textSearchTerm && showHeaderText && (
                          <span className={eventsFoundTitle}>
                            {headerText.indexOf('All') === 0
                              ? headerText
                              : `All ${formatedHeaderText}`}
                          </span>
                        )}
                        <div className={eventsFound}>
                          {totalResults} events found
                        </div>

                        <TileNavigation
                          locationLinks={locationLinksWithNearMe}
                          subCategoryLinks={distanceLinks}
                        />
                      </>
                    )}
                  </div>
                </>
              )}

              <Sort showBorder />

              <EventList
                onEventHoverOn={onEventHoverOn}
                onEventHoverOff={onEventHoverOff}
                className={list}
                query={query}
                events={events}
                source={trackingSource}
                onPaginationChange={scrollViewToTop}
                currentPageNumber={currentPage}
                total={totalResults}
                resultsPerPage={resultsPerPage}
              />

              {isMobile && (
                <CategoryBreadcrumbs
                  className={breadcrumbs}
                  categoryData={categoryData}
                />
              )}

              {description ? (
                <div className={categoryDescriptionStyle}>
                  <ReactMarkdown source={description} />
                </div>
              ) : null}

              <Faqs
                faqs={faqs}
                questionClass={question}
                answerClass={answer}
                hideAskQuestion
              />

              <RelatedLinks
                categoryLabel={categoryLabel}
                locationChildren={locationChildren}
                subLocationLinks={locationLinks}
              />
            </>
          )}
        </div>
      </Container>

      <div
        className={classnames({
          [footerStyles]: mapMode,
        })}
      >
        <Footer narrow={mapMode} />
      </div>
    </>
  );
};

export default flow(withLocationInfo, withSearchInfo)(BrowseLayout);
